import React, { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { accent6a as accent, accent6aRaw as accentRaw, useSlide6a, Seg } from "./slide6a.data";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";

/* Portrait/mobile layout for Slide 6a — flowing, scrollable, no canvas.
   Reads the same content as the desktop slide from slide6a.data.ts. */

const font = FONT_BODY;
const mono = FONT_MONO;

const lbl: CSSProperties = { fontFamily: mono, fontSize: 10, letterSpacing: 2.5, color: accent, textTransform: "uppercase", margin: "0 0 8px" };
const txt: CSSProperties = { fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 15, lineHeight: 1.65, color: "#cde0f2", margin: 0 };

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <b key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</b>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const HTLDSlide6aMobile: React.FC = () => {
  const {
    meta: slide6aMeta, causation: slide6aCausation,
    connected: slide6aConnected, buildup: slide6aBuildup, dreams: slide6aDreams,
  } = useSlide6a();
  const fontRef = useRef<HTMLLinkElement | null>(null);
  useEffect(() => {
    if (!document.querySelector('link[href*="Space+Grotesk"]')) {
      const l = document.createElement("link"); l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(l); fontRef.current = l;
    }
  }, []);

  return (
    <div style={{ background: "#040f18", color: "#d0e8f8", fontFamily: font, padding: "22px 18px 40px", boxSizing: "border-box", height: "100%", width: "100%", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* breadcrumb */}
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, color: `rgba(${accentRaw},0.7)`, textTransform: "uppercase", marginBottom: 8 }}>
        {slide6aMeta.breadcrumbPre} · <span style={{ color: accent }}>{slide6aMeta.breadcrumbAccent}</span>
      </div>

      {/* title */}
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600, fontSize: 25, lineHeight: 1.2, margin: "0 0 4px", color: "#e8f4ff" }}>
        {slide6aMeta.titlePre}<span style={{ color: accent }}>{slide6aMeta.titleAccentWord}</span>{slide6aMeta.titlePost}
      </h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontWeight: 300, fontSize: 14, color: `rgba(${accentRaw},0.75)`, margin: "0 0 20px", lineHeight: 1.5 }}>
        {slide6aMeta.subtitle}
      </p>

      {/* connected */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide6aMeta.connectedLabel}</div>
        <p style={txt}>{renderSegs(slide6aConnected, "#fac0d2")}</p>
      </div>

      {/* buildup */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide6aMeta.buildupLabel}</div>
        <p style={txt}>{renderSegs(slide6aBuildup, "#fac0d2")}</p>
      </div>

      {/* chain of causation — terminal panel */}
      <div style={{ marginBottom: 18 }}>
        <div style={lbl}>{slide6aMeta.chainLabel}</div>
        <div style={{ background: "rgba(0,0,0,0.30)", border: `1px solid rgba(${accentRaw},0.18)`, borderRadius: 8, overflow: "hidden" }}>
          {/* title bar */}
          <div style={{ background: `rgba(${accentRaw},0.08)`, borderBottom: `1px solid rgba(${accentRaw},0.14)`, padding: "6px 11px", display: "flex", alignItems: "center", gap: 5 }}>
            {["rgba(255,100,100,0.55)", "rgba(255,200,80,0.55)", `rgba(${accentRaw},0.55)`].map((c, i) => (
              <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: c }} />
            ))}
            <span style={{ fontSize: 8, fontFamily: mono, color: `rgba(${accentRaw},0.55)`, letterSpacing: 2, marginLeft: 4 }}>{slide6aMeta.chainBarLabel}</span>
          </div>
          {/* entries */}
          <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
            {slide6aCausation.map((c, i) => (
              <div key={i}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6, marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 5, minWidth: 0 }}>
                    <span style={{ color: accent, fontSize: 11, fontFamily: mono }}>▶</span>
                    <span style={{ fontSize: 9, fontFamily: mono, color: `rgba(${accentRaw},0.55)`, letterSpacing: 1 }}>{String(i + 1).padStart(2, "0")}_</span>
                    <span style={{ fontSize: 11, fontFamily: mono, color: accent, letterSpacing: 1.5, textTransform: "uppercase" }}>{c.stage}</span>
                  </div>
                  <span style={{ fontSize: 8.5, fontFamily: mono, color: `rgba(${accentRaw},0.52)`, letterSpacing: 1, flexShrink: 0 }}>{c.status}</span>
                </div>
                <p style={{ fontFamily: FONT_BODY, fontStyle: "normal", fontSize: 13, lineHeight: 1.45, color: "rgba(155,189,214,0.9)", margin: "0 0 6px", paddingLeft: 18 }}>{c.desc}</p>
                <div style={{ display: "flex", gap: 3, paddingLeft: 18 }}>
                  {Array.from({ length: 8 }, (_, bi) => (
                    <div key={bi} style={{ flex: 1, height: 5, borderRadius: 1, background: bi < c.bars ? `rgba(${accentRaw},0.85)` : `rgba(${accentRaw},0.1)` }} />
                  ))}
                </div>
                {i < slide6aCausation.length - 1 && (
                  <div style={{ height: 1, background: `rgba(${accentRaw},0.07)`, marginTop: 10 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* dreams callout */}
      <div style={{ background: `rgba(${accentRaw},0.06)`, border: `1px solid rgba(${accentRaw},0.22)`, borderRadius: 10, padding: "14px 16px" }}>
        <div style={lbl}>{slide6aMeta.dreamsLabel}</div>
        <p style={{ ...txt, color: "#f0c0d0" }}>{renderSegs(slide6aDreams, "#ffffff")}</p>
      </div>
    </div>
  );
};

export default HTLDSlide6aMobile;
